import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["chip", "group", "search", "selectedBanner", "selectedName", "clearBtn", "emptyState", "form", "submitBtn"];
  static values = { mood: { type: String, default: "" }, confirmIntro: String, confirmLabel: String };

  #initialId = null;
  #initialName = "";

  connect() {
    // Snapshot of what's actually applied right now (not what's checked as
    // the user clicks around) — used to gate the submit button and to fill
    // in the "from" side of the confirm message.
    const initialChecked = this.element.querySelector("input[type='radio']:checked");
    this.#initialId = initialChecked?.id || null;
    this.#initialName = this.#nameForChecked(initialChecked);

    this.#filterTracks();
    this.#syncBanner();
  }

  // ── Search input ───────────────────────────────────────────────────────────

  // Enter in the search field would otherwise submit the BGM form (apply
  // changes) instead of just narrowing the list.
  preventSearchSubmit(event) {
    event.preventDefault();
  }

  // ── Mood chip click ────────────────────────────────────────────────────────

  filterMood(event) {
    const mood = event.currentTarget.dataset.mood;
    this.moodValue = this.moodValue === mood ? "" : mood; // toggle
  }

  moodValueChanged() {
    this.#filterTracks();
  }

  filterSearch() {
    this.#filterTracks();
  }

  // ── Track selected (called via project-wizard#selectBgmTrack bubbling) ────

  trackSelected(event) {
    const row = event.currentTarget;
    const radio = row.querySelector("input[type='radio']");
    if (!radio) return;

    const nameEl = row.querySelector(".bgm-row__name");
    if (nameEl && this.hasSelectedNameTarget) {
      this.selectedNameTarget.textContent = nameEl.textContent;
    }
    this.#syncBanner();
  }

  // Modal reopened — a track clicked but never applied last time shouldn't
  // linger as "selected"; snap back to what's actually live, clear any
  // mood/search filter hiding it, and scroll it into view so the user
  // isn't dropped on the "All" list wondering where their current track is.
  onPickerOpened() {
    const initial = this.#initialId ? this.element.querySelector(`#${CSS.escape(this.#initialId)}`) : null;
    if (initial) initial.checked = true;

    if (this.hasSearchTarget) this.searchTarget.value = "";
    this.moodValue = "";
    this.#filterTracks();
    this.#syncBanner();

    const row = initial?.closest(".bgm-row");
    if (row) requestAnimationFrame(() => row.scrollIntoView({ block: "center" }));
  }

  // Native data-turbo-confirm only carries a plain string, which flattened
  // old/new track names into one uniform sentence — nothing told the eye
  // which name was leaving and which was arriving. This builds a proper
  // Contrast/Emphasis pair instead (muted+struck old, bold+accented new)
  // and routes through the shared confirm-dialog's messageHtml support.
  confirmApply(event) {
    event.preventDefault();
    if (!this.hasFormTarget || this.submitBtnTarget.disabled) return;

    const checked = this.element.querySelector(".bgm-row input[type='radio']:checked");
    const toName = this.#nameForChecked(checked);
    const intro = this.hasConfirmIntroValue ? `<p class="t-small t-slate">${this.#esc(this.confirmIntroValue)}</p>` : "";

    const messageHtml = `
      ${intro}
      <div class="confirm-diff">
        <span class="confirm-diff__old">${this.#esc(this.#initialName)}</span>
        <svg class="confirm-diff__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
        <span class="confirm-diff__new">${this.#esc(toName)}</span>
      </div>
    `;

    window.dispatchEvent(new CustomEvent("confirm-dialog:open", {
      detail: {
        messageHtml,
        confirmLabel: this.confirmLabelValue,
        confirmClass: "btn btn--primary",
        onConfirm: () => this.formTarget.requestSubmit(),
      },
    }));
  }

  clearSelection() {
    const noneRadio = this.element.querySelector("#bgm_track_none") || this.element.querySelector("#show_bgm_track_none");
    if (noneRadio) {
      noneRadio.checked = true;
      const modeField = document.querySelector("[data-project-wizard-target='bgmMode']");
      if (modeField) modeField.value = "none";
    }
    this.#syncBanner();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #filterTracks() {
    const mood = this.moodValue;
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : "";

    this.chipTargets.forEach((chip) => {
      const isAll = chip.dataset.mood === "";
      const active = mood === "" ? isAll : chip.dataset.mood === mood;
      chip.classList.toggle("bgm-chip--active", active);
      chip.setAttribute("aria-pressed", String(active));
    });

    let anyVisible = false;

    this.groupTargets.forEach((group) => {
      const moodMatch = mood === "" || group.dataset.mood === mood;
      let groupHasVisibleRow = false;

      group.querySelectorAll(".bgm-row").forEach((row) => {
        const name = row.dataset.name || "";
        const rowVisible = moodMatch && (!query || name.includes(query));
        row.hidden = !rowVisible;
        if (rowVisible) groupHasVisibleRow = true;
      });

      group.style.display = groupHasVisibleRow ? "" : "none";
      if (groupHasVisibleRow) anyVisible = true;
    });

    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = anyVisible;
  }

  #syncBanner() {
    if (!this.hasSelectedBannerTarget) return;

    const checked = this.element.querySelector(".bgm-row input[type='radio']:checked");
    const noneIds = ["bgm_track_none", "show_bgm_track_none"];
    const aiIds = ["bgm_track_ai"];
    const isNone = checked && noneIds.includes(checked.id);
    const isAiSelected = checked && aiIds.includes(checked.id);
    const isTrackSelected = checked && !isNone && !isAiSelected;
    const hasSelection = isTrackSelected || isAiSelected;

    // The clear button's visibility follows .bgm-selected--empty via CSS.
    this.selectedBannerTarget.classList.toggle("bgm-selected--empty", !hasSelection);

    if (this.hasSelectedNameTarget) {
      if (isAiSelected) {
        this.selectedNameTarget.textContent = this.selectedNameTarget.dataset.aiLabel || "AI Generate";
      } else if (!hasSelection) {
        this.selectedNameTarget.textContent = this.selectedNameTarget.dataset.emptyLabel || "No music";
      }
    }

    // Nothing to apply if the checked track is still the one already
    // playing on the video.
    if (this.hasSubmitBtnTarget) {
      const unchanged = checked?.id === this.#initialId;
      this.submitBtnTarget.disabled = unchanged;
    }
  }

  #esc(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  // Mirrors the AI/none/track-name branches in #syncBanner above, but
  // returns a plain string instead of writing into selectedNameTarget —
  // needed both for the live banner and for the frozen "from" snapshot.
  #nameForChecked(checked) {
    if (!checked) return this.hasSelectedNameTarget ? this.selectedNameTarget.dataset.emptyLabel || "No music" : "";
    if (checked.id === "bgm_track_ai") {
      return this.hasSelectedNameTarget ? this.selectedNameTarget.dataset.aiLabel || "AI Generate" : "";
    }
    if (["bgm_track_none", "show_bgm_track_none"].includes(checked.id)) {
      return this.hasSelectedNameTarget ? this.selectedNameTarget.dataset.emptyLabel || "No music" : "";
    }
    const row = checked.closest(".bgm-row");
    return row?.querySelector(".bgm-row__name")?.textContent || "";
  }
}
